import { useEffect, useState } from "react";
import { formatDate } from "@/lib/date-format";
import { useProjectStore } from "@/store/project-store";
import { endAdminAccess, useMockPlanAccess } from "./mock-plan-access";
import { subscribeBlockedEdit } from "./read-only-hint";
import { readsPublicPlan, usePlanVisibility } from "./plan-visibility";
import { useCan, useOpenPlanLifecycle, usePlanSurface, useWorkspaceAdminView } from "./use-plan-access";
import { chromeHeaderProps } from "@/shell/chrome-header";
import { useWorkspaceRole } from "./workspace-access";

/** 열람자 배너. 높이 28px, 작업 표면 위에 한 줄만 둔다 (§11.2). 관리자 접근이 있으면 그 안내가 대신 보인다. */
export function PlanReadOnlyBanner() {
  const surface = usePlanSurface();
  const adminView = useWorkspaceAdminView();
  const lifecycle = useOpenPlanLifecycle();
  const canRestore = useCan("plan.unarchive");
  const planId = useProjectStore((s) => s.activeId);
  const until = useMockPlanAccess().grants[planId ?? ""]?.until;
  const publicRead = readsPublicPlan(usePlanVisibility(planId), useWorkspaceRole());
  if (lifecycle === "archived") {
    return (
      <div
        {...chromeHeaderProps}
        role="status"
        className="flex h-7 shrink-0 items-center gap-2 border-b border-border bg-muted/40 px-3 text-[13px] leading-4 font-normal text-foreground/80"
      >
        <span className="rounded-sm bg-muted px-1.5 text-foreground">읽기 전용</span>
        <span>아카이브된 플랜입니다. 읽기만 할 수 있습니다</span>
        {canRestore && (
          <button type="button" className="ml-auto text-[13px] font-normal text-foreground" onClick={() => useProjectStore.getState().setLifecycle("active")}>
            복구
          </button>
        )}
      </div>
    );
  }
  if (adminView === "granted" && planId && until) {
    return (
      <div
        {...chromeHeaderProps}
        role="status"
        className="flex h-7 shrink-0 items-center gap-2 border-b border-border bg-muted/40 px-3 text-[13px] leading-4 font-normal text-foreground/80"
      >
        <span className="rounded-sm bg-muted px-1.5 text-foreground">읽기 전용</span>
        <span>관리자 접근 중. {formatDate(until, "numeric")}까지</span>
        <button type="button" className="ml-auto text-[13px] font-normal text-foreground" onClick={() => endAdminAccess(planId)}>
          종료
        </button>
      </div>
    );
  }
  if (surface.role !== "viewer" && !publicRead) return null;
  return (
    <div
      {...chromeHeaderProps}
      role="status"
      className="flex h-7 shrink-0 items-center gap-2 border-b border-border bg-muted/40 px-3 text-[13px] leading-4 font-normal text-foreground/80"
    >
      <span className="rounded-sm bg-muted px-1.5 text-foreground">읽기 전용</span>
      <span>
        {publicRead
          ? "공개 플랜입니다. 읽기만 할 수 있고 금액은 보이지 않습니다."
          : "열람자로 보고 있습니다. 편집하려면 관리자에게 요청하세요"}
      </span>
    </div>
  );
}

/** 막힌 편집 단축키의 한 번짜리 안내. */
export function BlockedEditHint() {
  const [message, setMessage] = useState<string | null>(null);
  useEffect(() => subscribeBlockedEdit(setMessage), []);
  useEffect(() => {
    if (!message) return;
    const timer = window.setTimeout(() => setMessage(null), 4000);
    return () => window.clearTimeout(timer);
  }, [message]);
  if (!message) return null;
  return (
    <div
      role="status"
      className="pointer-events-none fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-md border border-border bg-card px-3 py-1.5 text-[13px] font-normal text-foreground shadow-md"
    >
      {message}
    </div>
  );
}
